/**
 * BiriboxPredictiveSearch
 * 
 * Componente web personalizado para búsqueda predictiva en Shopify.
 * Utiliza la Section Rendering API para obtener resultados dinámicos y
 * permite la interacción con el carrito desde los resultados de búsqueda.
 * 
 * @author Biribox Team
 * @version 1.2.0
 */
class BiriboxPredictiveSearch extends HTMLElement {
    constructor() {
        super();
        // Configuración del componente
        this.selectors = {
            results: '.biribox-predictive-search__results',
            close: '.biribox-predictive-search__close',
            modal: '.biribox-predictive-search',
            searchInput: 'input.biribox-predictive[name="q"]',
            content: '.biribox-predictive-search__content',
            addToCart: '.add-to-cart',
            quantityContainer: '.list-quantity-inside-product',
            increaseBtn: '[data-action="increase"]',
            decreaseBtn: '[data-action="decrease"]'
        };
        
        // Estado del componente
        this.resultsContainer = null;
        this.searchInput = null;
        this.debounceTimer = null;
        this.isOpen = false;
        
        // Configuración de la búsqueda
        this.minSearchLength = this.getAttribute("min-search-length");
        this.debounceDelay = 300;
        this.maxResults = this.getAttribute("max-results");
        
        // Inicializar
        this.initializeComponent();
    }
    
    /**
     * Inicializa el componente cargando el template y configurando referencias
     */
    initializeComponent() {
        const template = document.getElementById('biribox-predictive-search-template');
        
        if (!template) {
            console.error('Error: No se encontró el template para biribox-predictive-search');
            return;
        }
        
        // Clonar el contenido del template
        const templateContent = template.content.cloneNode(true);
        this.appendChild(templateContent);
        
        // Guardar referencia al contenedor de resultados
        this.resultsContainer = this.querySelector(this.selectors.results);
    }
    
    /**
     * Método del ciclo de vida que se ejecuta cuando el componente se añade al DOM
     */
    connectedCallback() {
        this.setupEventListeners();
    }
    
    /**
     * Configura todos los event listeners necesarios
     */
    setupEventListeners() {
        // Buscar todos los campos de búsqueda en la página
        const searchInputs = document.querySelectorAll(this.selectors.searchInput);
        
        if (searchInputs.length === 0) {
            return;
        }
        
        // Añadir evento a cada input encontrado
        searchInputs.forEach(input => {
            input.addEventListener('input', this.handleInput.bind(this));
        });
        
        // Guardar referencia al primer input (para cerrar el modal)
        this.searchInput = searchInputs[0];
        
        // Evento para cerrar el modal
        const closeButton = this.querySelector(this.selectors.close);
        if (closeButton) {
            closeButton.addEventListener('click', () => this.close());
        }
        
        // Evento para cerrar el modal al hacer clic fuera de los resultados
        const modal = this.querySelector(this.selectors.modal);
        if (modal) {
            modal.addEventListener('click', (event) => {
                const resultsContainer = this.querySelector(this.selectors.results);
                // Si el clic fue directamente en el modal (fuera de los resultados), cerrar
                if (event.target === modal) {
                    this.close();
                }
            });
        }
        
        // Eventos para cerrar el modal
        document.addEventListener('click', this.handleOutsideClick.bind(this));
        document.addEventListener('keydown', this.handleKeydown.bind(this));
    }
    
    /**
     * Maneja el evento de input en el campo de búsqueda
     * @param {Event} event - Evento de input
     */
    handleInput(event) {
        const searchTerm = event.target.value.trim();
        
        // Limpiar el timer anterior para implementar debounce
        clearTimeout(this.debounceTimer);
        
        // No buscar si el término es muy corto
        if (searchTerm.length < this.minSearchLength) {
            this.clearResults();
            return;
        }
        
        // Esperar antes de realizar la búsqueda (debounce)
        this.debounceTimer = setTimeout(() => {
            this.performSearch(searchTerm);
        }, this.debounceDelay);
    }
    
    /**
     * Limpia los resultados de búsqueda
     */
    clearResults() {
        if (this.resultsContainer) {
            this.close();
        }
    }
    
    /**
     * Maneja clics fuera del componente para cerrarlo
     * @param {Event} event - Evento de clic
     */
    handleOutsideClick(event) {
        // No cerrar si el clic fue en un botón de agregar o eliminar del carrito
        if (event.target.closest(this.selectors.addToCart) || 
            event.target.closest(this.selectors.increaseBtn) || 
            event.target.closest(this.selectors.decreaseBtn)) {
            return;
        }
        
        // Verificar si el clic fue en el botón de cierre
        const isCloseButton = event.target.closest(this.selectors.close);
        
        // Verificar si el clic fue en el overlay negro (directamente en el elemento modal)
        const isOverlay = event.target.classList.contains('biribox-predictive-search');
        
        // Cerrar solo si el clic fue en el botón de cierre o en el overlay negro
        if (isCloseButton || isOverlay) {
            this.close();
        }
    }
    
    /**
     * Maneja eventos de teclado (ESC para cerrar)
     * @param {KeyboardEvent} event - Evento de teclado
     */
    handleKeydown(event) {
        if (event.key === 'Escape' && this.isOpen) {
            this.close();
        }
    }
        
    /**
     * Realiza la búsqueda utilizando la Section Rendering API de Shopify
     * @param {string} searchTerm - Término de búsqueda
     */
    performSearch(searchTerm) {
        // Abrir el modal y mostrar estado de carga
        this.open();
        this.showLoading();
                
        const searchUrl = `${window.Shopify.routes.root}search?section_id=main-search&q=${encodeURIComponent(searchTerm)}`;
        
        fetch(searchUrl)
            .then(response => response.ok ? response.text() : Promise.reject(response))
            .then(html => {
                // console.log(html, searchTerm)

                this.processResults(html, searchTerm);
                // Después de procesar los resultados principales, obtenemos las sugerencias
                this.fetchSuggestions(searchTerm);
            })
            .catch(error => {
                console.error('Error en la búsqueda predictiva:', error);
                this.showError();
            });
    }
    
    /**
     * Muestra el indicador de carga
     */
    showLoading() {
        if (!this.resultsContainer) return;
        
        // Preservar el botón de cierre
        const closeButton = this.resultsContainer.querySelector(this.selectors.close);
        const title = this.resultsContainer.querySelector('h2');
        this.resultsContainer.innerHTML = '';
        
        // Volver a añadir el botón de cierre
        if (closeButton) {
            this.resultsContainer.appendChild(closeButton);
        }
        
        if (title) {
            this.resultsContainer.appendChild(title);
        }
        
        this.resultsContainer.insertAdjacentHTML('beforeend', `
            <div class="biribox-predictive-search__loading">
                <div></div>
            </div>
        `);
    }
    
    /**
     * Muestra un mensaje de error
     */
    showError() {
        if (!this.resultsContainer) return;
        
        // Preservar el botón de cierre
        const closeButton = this.resultsContainer.querySelector(this.selectors.close);
        const title = this.resultsContainer.querySelector('h2');
        this.resultsContainer.innerHTML = '';
        
        // Volver a añadir el botón de cierre
        if (closeButton) {
            this.resultsContainer.appendChild(closeButton);
        }
        
        if (title) {
            this.resultsContainer.appendChild(title);
        }
        
        this.resultsContainer.insertAdjacentHTML('beforeend', `
            <div class="biribox-predictive-search__message biribox-predictive-search__message--error">
                <p>Error al cargar los resultados</p>
            </div>
        `);
    }
    
    /**
     * Procesa los resultados de la búsqueda
     * @param {string} html - HTML con los resultados
     * @param {string} searchTerm - Término de búsqueda
     */
    processResults(html, searchTerm) {
        // Parsear el HTML de respuesta
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const products = doc.querySelectorAll('[data-product-search] .c-cp');
        
        // Limpiar y preparar el contenedor principal
        // Preservar el botón de cierre
        const closeButton = this.resultsContainer.querySelector(this.selectors.close);
        const title = this.resultsContainer.querySelector('h2');
        this.resultsContainer.innerHTML = '';
        
        // Volver a añadir el botón de cierre
        if (closeButton) {
            this.resultsContainer.appendChild(closeButton);
        }
        
        if (title) {
            // Obtener textos de traducción con valores por defecto en caso de que no existan
            const resultsText = title.dataset.translateResults || 'Resultados para';
            const noResultsText = title.dataset.translateNoResults || 'No se encontraron resultados para';
            
            // Establecer el texto según si hay resultados o no
            title.textContent = products.length > 0 
                ? `${resultsText} "${searchTerm}"`
                : `${noResultsText} "${searchTerm}"`;
                
            this.resultsContainer.appendChild(title);
        }
        
        // Crear la estructura de contenedores
        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'biribox-predictive-search__content-wrapper c-ps__list';
        
        const contentContainer = document.createElement('div');
        contentContainer.className = 'biribox-predictive-search__content c-ps__results';
        
        const suggestionsContainer = document.createElement('div');
        suggestionsContainer.className = 'biribox-predictive-search__suggestions c-ps__suggestions';
        
        const viewAllContainer = document.createElement('div');
        viewAllContainer.className = 'biribox-predictive-search__view-all c-ps__view-all';
        
        // Añadir los contenedores al DOM
        contentWrapper.appendChild(suggestionsContainer);
        contentWrapper.appendChild(contentContainer);
        this.resultsContainer.appendChild(contentWrapper);
        this.resultsContainer.appendChild(viewAllContainer);
        
        // Mostrar resultados o mensaje de no resultados
        if (products.length > 0) {
            this.renderProducts(products, searchTerm, contentContainer, viewAllContainer);
        } else {
            this.showNoResults(searchTerm, contentContainer);
        }
    }

      /**
     * Obtiene las sugerencias y colecciones usando la API de Predictive Search
     * @param {string} searchTerm - Término de búsqueda
     */
    fetchSuggestions(searchTerm) {
        // URL para la API de Predictive Search - usamos la API de secciones
        const suggestUrl = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(searchTerm)}&resources[type]=query,collection&section_id=predictive-search`;
        
        fetch(suggestUrl)
            .then(response => response.ok ? response.text() : Promise.reject(response))
            .then(html => {
                this.processSuggestions(html);
            })
            .catch(error => {
                console.error('Error al obtener sugerencias:', error);
            });
    }

    /**
     * Procesa las sugerencias y colecciones
     * @param {string} html - HTML con las sugerencias y colecciones
     */
    processSuggestions(html) {
        if (!html || html.trim() === '') return;
        
        try {
            // Extraer el contenido de la sección predictive-search
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const predictiveSearchContent = doc.getElementById('predictive-search-results');
            
            if (!predictiveSearchContent) return;
            
            // Buscar el contenedor de sugerencias en la nueva estructura
            const resultsList = this.resultsContainer.querySelector('.c-ps__list')
            const suggestionsContainer = this.resultsContainer.querySelector('.biribox-predictive-search__suggestions');
            const contentContainer = this.resultsContainer.querySelector('.biribox-predictive-search__content');
            
            if (!suggestionsContainer || !contentContainer) return;
            
            // Insertar el HTML de la sección predictive-search
            suggestionsContainer.innerHTML = predictiveSearchContent.innerHTML;
            
            // Verificar si hay sugerencias o colecciones
            const hasSuggestions = suggestionsContainer.querySelector('.predictive-search__item-group');
            
            // Ajustar el ancho del contenido según si hay sugerencias o no
            if (!hasSuggestions) {
                // No hay sugerencias, ocultar el sidebar y hacer que el contenido ocupe el 100%
                suggestionsContainer.style.display = 'none';
                // contentContainer.style.width = '100%';
                resultsList.classList.remove('double')
            } else {
                // Hay sugerencias, mostrar el sidebar y mantener el ancho original
                suggestionsContainer.style.display = 'block';
                resultsList.classList.add('double')
                // contentContainer.style.width = '75%';
            }
            
            // Configurar eventos para los resultados
            const resultLinks = suggestionsContainer.querySelectorAll('.predictive-search__item');
            resultLinks.forEach(link => {
                link.addEventListener('click', () => this.close());
            });
        } catch (error) {
            console.error('Error al procesar las sugerencias:', error);
        }
    }
    
    /**
     * Renderiza los productos en el contenedor de resultados
     * @param {NodeList} products - Lista de productos
     * @param {string} searchTerm - Término de búsqueda
     * @param {HTMLElement} contentContainer - Contenedor para los productos
     * @param {HTMLElement} viewAllContainer - Contenedor para el enlace de ver todos
     */
    renderProducts(products, searchTerm, contentContainer, viewAllContainer) {
        // Crear grid de resultados
        const resultsGrid = document.createElement('div');
        resultsGrid.className = 'biribox-predictive-search__grid c-ps__results__grid';
        
        // Obtener el estado actual del carrito para mostrar correctamente los botones
        this.getCartState().then(cartItems => {
            // Añadir productos limitados por maxResults
            const maxProducts = Math.min(products.length, this.maxResults);
            
            for (let i = 0; i < maxProducts; i++) {
                // Clonar el producto para evitar problemas con el DOM
                const productClone = products[i].cloneNode(true);
                
                // Actualizar el estado de los botones según el carrito
                this.updateProductButtonsState(productClone, cartItems);
                
                // Agregar el producto al grid
                resultsGrid.appendChild(productClone);
            }
            
            // Añadir el grid al contenedor de contenido
            contentContainer.appendChild(resultsGrid);

            let customerType = window.localStorage.getItem('ls.biribox.view-type');
            if (!customerType) {
              document.getElementById('overlay-buyer').style.display = 'block';
            } else {
              showCustomerTypeContent(customerType)
            }
            
            // Añadir enlace para ver todos los resultados si hay más
            if (products.length > this.maxResults) {
                this.addViewAllLink(searchTerm, products.length, viewAllContainer);
            }
            
            // Configurar los eventos del carrito
            this.setupCartEvents();

            cartItems.forEach(item => {
                this.updateProductCardPrice(item.id, item.final_price);
            });
            
        }).catch(error => {
            console.error('Error al obtener el estado del carrito:', error);
            
            // Fallback en caso de error: renderizar sin verificar el carrito
            const maxProducts = Math.min(products.length, this.maxResults);
            
            for (let i = 0; i < maxProducts; i++) {
                resultsGrid.appendChild(products[i].cloneNode(true));
            }
            

            
            // Añadir el grid al contenedor de contenido
            contentContainer.appendChild(resultsGrid);
            
            // Añadir enlace para ver todos los resultados si hay más
            if (products.length > this.maxResults) {
                this.addViewAllLink(searchTerm, products.length, viewAllContainer);
            }
            
            // Configurar los eventos del carrito incluso en caso de error
            this.setupCartEvents();
        });
    }

    /**
     * Configura los eventos relacionados con el carrito en los resultados de búsqueda
     */
    setupCartEvents() {
        try {
            // Referencia al contenedor de resultados para limitar el alcance de la delegación
            const resultsContainer = this.resultsContainer;
            if (!resultsContainer) return;
            
            // Creamos un único manejador de eventos que delegará a las funciones apropiadas
            const clickHandler = (event) => {
                // Verificar si el clic fue en un botón de agregar al carrito
                const addToCartButton = event.target.closest(this.selectors.addToCart);
                if (addToCartButton && resultsContainer.contains(addToCartButton)) {
                    this.handleAddToCart(event);
                    return;
                }
                
                // Verificar si el clic fue en un botón de incrementar cantidad
                const increaseButton = event.target.closest(this.selectors.increaseBtn);
                if (increaseButton && resultsContainer.contains(increaseButton)) {
                    this.handleIncreaseQuantity(event);
                    return;
                }
                
                // Verificar si el clic fue en un botón de decrementar cantidad
                const decreaseButton = event.target.closest(this.selectors.decreaseBtn);
                if (decreaseButton && resultsContainer.contains(decreaseButton)) {
                    this.handleDecreaseQuantity(event);
                    return;
                }
            };
            
            // Almacenar el manejador para poder eliminarlo más tarde si es necesario
            this._cartEventsHandler = clickHandler;
            
            // Eliminar el manejador anterior si existe
            if (this._previousCartEventsHandler) {
                document.removeEventListener('click', this._previousCartEventsHandler);
            }
            
            // Añadir el nuevo manejador
            document.addEventListener('click', this._cartEventsHandler);
            this._previousCartEventsHandler = this._cartEventsHandler;
            
        } catch (error) {
            console.error('Error al configurar la delegación de eventos:', error);
        }
    }
    
    /**
     * Maneja el evento de agregar al carrito
     * @param {Event} evt - Evento de clic
     */
    handleAddToCart(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        
        const button = evt.target.closest(this.selectors.addToCart);
        const productContainer = button.closest('.product-description, .card');
        const variantInput = productContainer.querySelector('input[name="productId"]');
        const variantId = variantInput ? variantInput.value : null;
        const title = productContainer.querySelector('.c-cp__title').textContent;

        if (!variantId) {
            console.error('No se pudo encontrar el ID de la variante para el producto');
            return;
        }
        
        // Usar el método addToCart para agregar el producto
        this.addToCart(variantId)
            .then(data => {
                // Ocultar el botón de agregar al carrito
                button.style.display = 'none';
                
                // Mostrar el contenedor de cantidad
                const quantityContainer = productContainer.querySelector(this.selectors.quantityContainer);
                if (quantityContainer) {
                    quantityContainer.classList.remove('hidden');
                    const quantityText = quantityContainer.querySelector('h3');
                    if (quantityText) {
                        quantityText.textContent = '1';
                    }
                }
                
                // Mostrar el toast de confirmación
                this.showAddToCartToast(title);
            })
            .catch(error => {
                console.error('Error al agregar al carrito:', error);
                alert('Error al agregar al carrito: ' + error.message);
            });
    }
    
    /**
     * Maneja el evento de incrementar la cantidad de un producto
     * @param {Event} evt - Evento de clic
     */
    handleIncreaseQuantity(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        
        const button = evt.target.closest(this.selectors.increaseBtn);
        const variantId = button.getAttribute('data-variant-id');
        const quantityContainer = button.closest(this.selectors.quantityContainer);
        const quantityText = quantityContainer.querySelector('h3');
        let currentQty = parseInt(quantityText.textContent);
        currentQty++;
        
        // Actualizar la UI inmediatamente para una mejor experiencia
        quantityText.textContent = currentQty;
        
        // Actualizar el carrito
        this.updateCartItem(variantId, currentQty)
            .catch(error => {
                // Revertir el cambio en la UI en caso de error
                quantityText.textContent = currentQty - 1;
            });
    }
    
    /**
     * Maneja el evento de decrementar la cantidad de un producto
     * @param {Event} evt - Evento de clic
     */
    handleDecreaseQuantity(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        
        const button = evt.target.closest(this.selectors.decreaseBtn);
        const variantId = button.getAttribute('data-variant-id');
        const quantityContainer = button.closest(this.selectors.quantityContainer);
        const quantityText = quantityContainer.querySelector('h3');
        let currentQty = parseInt(quantityText.textContent);
        
        if (currentQty > 1) {
            // Si hay más de 1, simplemente decrementar
            currentQty--;
            quantityText.textContent = currentQty;
            
            // Actualizar el carrito
            this.updateCartItem(variantId, currentQty)
                .catch(error => {
                    // Revertir el cambio en la UI en caso de error
                    quantityText.textContent = currentQty + 1;
                });
        } else if (currentQty === 1) {
            // Si hay solo 1, eliminar del carrito y restaurar el botón
            quantityContainer.classList.add('hidden');
            
            // Buscar todos los botones de agregar al carrito para este producto
            const productContainer = quantityContainer.closest('.product-description, .card');
            const addButtons = productContainer.querySelectorAll(this.selectors.addToCart);
            
            // Mostrar todos los botones de agregar al carrito
            addButtons.forEach(button => {
                button.style.display = '';
            });
            
            // Resetear el contador a 1 para la próxima vez
            quantityText.textContent = '1';
            
            // Eliminar el producto del carrito
            this.updateCartItem(variantId, 0)
                .catch(error => {
                    // Revertir el cambio en la UI en caso de error
                    quantityContainer.classList.remove('hidden');
                    addButtons.forEach(button => {
                        button.style.display = 'none';
                    });
                });
        }
    }
    
    /**
     * Actualiza el estado de los botones de un producto según el carrito
     * @param {HTMLElement} productElement - Elemento del producto
     * @param {Array} cartItems - Items del carrito
     */
    updateProductButtonsState(productElement, cartItems) {
        // Buscar el input con el ID de la variante
        const variantInput = productElement.querySelector('input[name="productId"]');
        if (!variantInput || !variantInput.value) return;
        
        const variantId = variantInput.value;
        
        // Buscar el item en el carrito
        const cartItem = cartItems.find(item => item.id.toString() === variantId.toString());
        
        // Buscar los elementos relevantes
        const addToCartButtons = productElement.querySelectorAll(this.selectors.addToCart);
        const quantityContainer = productElement.querySelector(this.selectors.quantityContainer);
        
        if (!quantityContainer) return;
        
        const quantityText = quantityContainer.querySelector('h3');
        
        if (cartItem && cartItem.quantity > 0) {
            // El producto está en el carrito
            addToCartButtons.forEach(button => button.style.display = 'none');
            quantityContainer.classList.remove('hidden');
            
            if (quantityText) {
                quantityText.textContent = cartItem.quantity.toString();
            }
        } else {
            // El producto no está en el carrito
            addToCartButtons.forEach(button => button.style.display = '');
            quantityContainer.classList.add('hidden');
            
            if (quantityText) {
                quantityText.textContent = '1';
            }
        }
    }
    
    /**
     * Obtiene el estado actual del carrito
     * @returns {Promise<Array>} - Promesa que resuelve con los items del carrito
     */
    getCartState() {
        return fetch('/cart.js')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Error al obtener el carrito');
                }
                return response.json();
            })
            .then(cart => cart.items || [])
            .catch(error => {
                console.error('Error al obtener el estado del carrito:', error);
                return []; // Devolver un array vacío en caso de error
            });
    }
    
    /**
     * Normaliza el ID de la variante para asegurar que tenga el formato correcto
     * @param {string|number} variantId - ID de la variante a normalizar
     * @returns {number} - ID de la variante normalizado
     */
    normalizeVariantId(variantId) {
        if (typeof variantId === 'string') {
            // Eliminar cualquier prefijo como 'gid://shopify/ProductVariant/'
            if (variantId.includes('/')) {
                variantId = variantId.split('/').pop();
            }
            // Convertir a número entero
            return parseInt(variantId, 10);
        }
        return variantId; // Si ya es un número, devolverlo tal cual
    }

    updateProductCardPrice(variantId, finalPrice) {
        const cardProduct = this.querySelector(`.product-description[data-id="${variantId}"]`);

        if (!cardProduct) return;
      
        const priceBoxPrivate = cardProduct.querySelector('.c-cp__price__pricebox[data-private-event]');
        const priceBoxBusiness = cardProduct.querySelector('.c-cp__price__pricebox[data-business-event]');
        const pricePerUnitPrivate = cardProduct.querySelector('.c-cp__price__perunit__t[data-private-event]');
        const pricePerUnitBusiness = cardProduct.querySelector('.c-cp__price__perunit__t[data-business-event]');
      
        if (priceBoxPrivate) {
          const unitPerTypeAttr = parseFloat(priceBoxPrivate.getAttribute('data-unit-per-type'));
          const ivaAttr = priceBoxPrivate.getAttribute('data-iva');

          const iva = parseFloat(ivaAttr);
          const priceEl = priceBoxPrivate.querySelector('.c-cp__price__pricebox__price');
      
          if (priceEl && !isNaN(iva)) {
            const priceWithIva = parseFloat(finalPrice) * (1 + iva / 100);
            const priceWithUnit = priceWithIva / unitPerTypeAttr;

            priceEl.textContent = `${Shopify.formatMoney(priceWithIva, "{{amount}}€")}`;

            if (pricePerUnitPrivate) {
              pricePerUnitPrivate.textContent = `(${Shopify.formatMoney(priceWithUnit, "{{amount}}€")} / ud)`;
            }
          }
          
          if (priceBoxBusiness) {
            const priceEl = priceBoxBusiness.querySelector('.c-cp__price__pricebox__price');
            const priceWithUnit = parseFloat(finalPrice) / unitPerTypeAttr;
  
            if (priceEl) {
              priceEl.textContent = `${Shopify.formatMoney(finalPrice, "{{amount}}€")}`;
            }
  
            if(pricePerUnitBusiness){
              pricePerUnitBusiness.textContent = `(${Shopify.formatMoney(priceWithUnit, "{{amount}}€")} / ud)`;
            }
          }
        }
      
    }

    /**
     * Actualiza un item en el carrito
     * @param {string|number} variantId - ID de la variante
     * @param {number} quantity - Nueva cantidad
     * @returns {Promise} - Promesa que resuelve con los datos del carrito actualizado
     */
    updateCartItem(variantId, quantity) {
        // Normalizar el ID de la variante
        const normalizedId = this.normalizeVariantId(variantId);
        
        // Verificar que el ID sea válido
        if (isNaN(normalizedId)) {
            console.error('ID de variante inválido:', variantId);
            return Promise.reject(new Error('ID de variante inválido'));
        }
        
        // Crear FormData para la solicitud
        const formData = new FormData();
        formData.append('id', normalizedId);
        formData.append('quantity', quantity);
        
        return fetch('/cart/change.js', {
            method: 'POST',
            body: formData
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Error en la respuesta: ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            // Disparar evento para actualizar el carrito
            console.log('CART:REFRESH 3')
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            data.items.forEach(item => {
                if (item.id.toString() === variantId.toString()) {
                    this.updateProductCardPrice(variantId, item.final_price);
                }
            });
            return data;
        })
        .catch(error => {
            console.error('Error al actualizar el carrito:', error);
            window.showToast(window.globalTranslations.errorAddToCart, 'error')
            throw error;
        });
    }
    
    /**
     * Añade un producto al carrito
     * @param {string|number} variantId - ID de la variante
     * @param {number} quantity - Cantidad a añadir
     * @returns {Promise} - Promesa que resuelve con los datos del producto añadido
     */
    addToCart(variantId, quantity = 1) {
        // Normalizar el ID de la variante
        const normalizedId = this.normalizeVariantId(variantId);
        
        // Verificar que el ID sea válido
        if (isNaN(normalizedId)) {
            console.error('ID de variante inválido:', variantId);
            return Promise.reject(new Error('ID de variante inválido'));
        }
        
        // Crear FormData para la solicitud
        const formData = new FormData();
        formData.append('id', normalizedId);
        formData.append('quantity', quantity);
        
        return fetch('/cart/add.js', {
            method: 'POST',
            body: formData
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Error en la respuesta: ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            // Disparar evento para actualizar el carrito
            console.log('CART:REFRESH 4')
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            this.updateProductCardPrice(variantId, data.final_price);
            return data;
        })
        .catch(error => {
            console.error('Error al agregar al carrito:', error);
            throw error;
        });
    }
    
     /**
     * Añade un enlace para ver todos los resultados
     * @param {string} searchTerm - Término de búsqueda
     * @param {number} count - Número total de resultados
     * @param {HTMLElement} viewAllContainer - Contenedor para el enlace de ver todos
     */
     addViewAllLink(searchTerm, count, viewAllContainer) {

        const allResultsText = this.querySelector('.biribox-predictive-search__results').dataset.buttonTranslate || 'Ver todos los resultados';
        viewAllContainer.innerHTML = `
            <a href="${window.location.pathname}?q=${encodeURIComponent(searchTerm)}" class="c-btn c-btn--type-solid">
                ${allResultsText} (${count})
            </a>
        `;
    }
    
    /**
     * Muestra un mensaje cuando no hay resultados
     * @param {string} searchTerm - Término de búsqueda
     * @param {HTMLElement} contentContainer - Contenedor para el mensaje de no resultados
     */
    showNoResults(searchTerm, contentContainer) {
        contentContainer.innerHTML = `
            <div class="biribox-predictive-search__message biribox-predictive-search__message--no-results">
                <p>No se encontraron productos para "${searchTerm}"</p>
            </div>
        `;
    }
    
    /**
     * Abre el modal de resultados
     * 
     * 
     */
    open() {
        if (this.isOpen) return;
        
        const modal = this.querySelector(this.selectors.modal);
        if (!modal) return;
        
        modal.classList.add('active');
        document.body.classList.add('overflow-hidden')
        this.isOpen = true;
    }
    
    /**
     * Muestra un toast de confirmación al añadir un producto al carrito
     */
    showAddToCartToast(title) {
        // Usar la función global showToast si está disponible
        let messageToast = window.globalTranslations?.addToCart || 'Producto añadido al carrito';
        if (typeof window.showToast === 'function') {
            window.showToast(`${messageToast}: ${title}`);
        } else {
            // Implementación simple de toast con JavaScript puro
            const toast = document.createElement('div');
            toast.className = 'biribox-toast';
            toast.innerHTML = `
                <div class="biribox-toast__content">
                    <p class="text-white">${window.globalTranslations?.addToCart || 'Producto añadido al carrito'}</p>
                </div>
            `;
            document.body.appendChild(toast);
            
            // Añadir estilos inline si no existen en CSS
            toast.style.position = 'fixed';
            toast.style.top = '20px';
            toast.style.right = '20px';
            toast.style.backgroundColor = '#4CAF50';
            toast.style.color = 'white';
            toast.style.padding = '16px';
            toast.style.borderRadius = '4px';
            toast.style.zIndex = '9999';
            toast.style.opacity = '0';
            toast.style.transition = 'opacity 0.5s';
            
            // Mostrar el toast
            setTimeout(() => {
                toast.style.opacity = '1';
            }, 10);
            
            // Ocultar y eliminar después de 5 segundos
            setTimeout(() => {
                toast.style.opacity = '0';
                setTimeout(() => {
                    if (toast.parentNode) {
                        toast.parentNode.removeChild(toast);
                    }
                }, 500);
            }, 5000);
        }
    }
    
    /**
     * Cierra el modal de resultados
     */
    close() {
        const container = this.querySelector(this.selectors.modal);
        if (container) {
            container.classList.remove('active');
            document.body.classList.remove('overflow-hidden')
            this.isOpen = false;
            
            // Restaurar el estado original del input de búsqueda
            const searchInput = document.querySelector(this.selectors.searchInput);
            if (searchInput) {
                // Restaurar el z-index original
                searchInput.style.position = '';
                searchInput.style.zIndex = '';
            }
        }
    }
}

// Registrar el componente web cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('biribox-predictive-search')) {
        customElements.define('biribox-predictive-search', BiriboxPredictiveSearch);
    }
});